import React, { useState, useEffect } from 'react';
import { 
  ComposedChart, XAxis, YAxis, 
  CartesianGrid, Tooltip, ResponsiveContainer,
  Line, Scatter
} from 'recharts';
import { ProjectGraphData } from '@/types/project';
import { Skeleton } from '@/components/ui/skeleton';

interface GraphDataPoint {
  date: string;
  marketValue?: number;      // Spot FDV
  fundingValue?: number;     // Funding FDV  
  secondLaneBuy?: number;    // Buy orders
  secondLaneSell?: number;   // Sell orders
  marketCap?: number;        // Market cap for tooltip
  allBuyOrders?: OrderData[];
  allSellOrders?: OrderData[];
}

interface OrderData {
  id: string;
  fdv: number;
  amount: number;
}

interface ProjectChartProps {
  graphData: ProjectGraphData | null;
  loading?: boolean;
}

const formatCurrency = (value: number) => {
  if (value >= 1_000_000_000) return `$${(value / 1_000_000_000).toFixed(1)}B`;
  if (value >= 1_000_000) return `$${(value / 1_000_000).toFixed(1)}M`;
  if (value >= 1_000) return `$${(value / 1_000).toFixed(1)}K`;
  return `$${value}`;
};

// Custom square shape for scatter points
interface ScatterShapeProps {
  cx?: number;
  cy?: number;
  fill?: string;
  payload?: GraphDataPoint;
  dataKey?: string;
}

const SquareShape = (props: ScatterShapeProps) => {
  const { cx, cy, fill, payload, dataKey } = props;
  
  // Check if we have valid coordinates
  if (cx === undefined || cy === undefined) return null;
  
  // Check if the payload has actual data for this dataKey
  if (payload && dataKey) {
    const value = payload[dataKey as keyof GraphDataPoint];
    if (value === undefined || value === null) return null;
  }
  
  const size = 14;
  return (
    <rect
      x={cx - size / 2}
      y={cy - size / 2}
      width={size}
      height={size}
      fill={fill}
    />
  );
};

// Function for processing price history
const processPriceHistory = (
  priceHistory: ProjectGraphData['price_history'] = [],
  dataMap: Map<string, GraphDataPoint>
): Map<string, GraphDataPoint> => {
  if (priceHistory.length) {
    priceHistory.forEach(point => {
      // Only add points with non-zero spot FDV values
      if (point.spot_fdv_usd && point.spot_fdv_usd > 0) {
        dataMap.set(point.date, {
          date: point.date,
          marketValue: point.spot_fdv_usd,
          marketCap: point.market_cap_usd // Store market cap for tooltip
        });
      }
    });
  }
  
  return dataMap;
};

// Function for processing funding rounds
const processFundingRounds = (
  fundingRounds: ProjectGraphData['funding_rounds'] = [],
  dataMap: Map<string, GraphDataPoint>
): Map<string, GraphDataPoint> => {
  if (fundingRounds.length) {
    let currentValuation = 0;
    fundingRounds.forEach(round => {
      if (round.valuation !== null) {
        currentValuation = round.valuation;
      }
      
      const entry = dataMap.get(round.date) || { date: round.date };
      entry.fundingValue = currentValuation;
      dataMap.set(round.date, entry);

      // Process fill data
      round.fill?.forEach(fillPoint => {
        const fillEntry = dataMap.get(fillPoint.date) || { date: fillPoint.date };
        fillEntry.fundingValue = fillPoint.valuation ?? currentValuation;
        dataMap.set(fillPoint.date, fillEntry);
      });
    });
  }
  
  return dataMap;
};

// Function for processing orders
const processOrders = (
  orders: ProjectGraphData['orders'] = [],
  dataMap: Map<string, GraphDataPoint>
): Map<string, GraphDataPoint> => {
  const orderMapBuy = new Map<string, number>();
  const orderMapSell = new Map<string, number>();
  const allOrdersMap = new Map<string, { buys: OrderData[], sells: OrderData[] }>();

      if (orders.length) {
      orders.forEach(order => {
        const date = order.date;
        const fdv = order.offered_fully_diluted_valuation;
        const amount = order.offered_amount;
        const id = order.order_id;
        
        // Collecting all orders for each date
        if (!allOrdersMap.has(date)) {
          allOrdersMap.set(date, { buys: [], sells: [] });
        }
        const ordersForDate = allOrdersMap.get(date)!;
        
        if (order.type.includes('BUY') || order.type === 'OrderType.BUY') {
          // For BUY orders we take the minimum FDV value
          if (!orderMapBuy.has(date) || fdv < orderMapBuy.get(date)!) {
            orderMapBuy.set(date, fdv);
          }
          ordersForDate.buys.push({ id, fdv, amount });
        } else if (order.type.includes('SELL') || order.type === 'OrderType.SELL') {
          // For SELL orders we take the maximum FDV value
          if (!orderMapSell.has(date) || fdv > orderMapSell.get(date)!) {
            orderMapSell.set(date, fdv);
          }
          ordersForDate.sells.push({ id, fdv, amount });
        }
      });

    // Apply collected values to dataMap
    for (const [date, fdv] of orderMapBuy.entries()) {
      const entry = dataMap.get(date) || { date };
      entry.secondLaneBuy = fdv;
      entry.allBuyOrders = allOrdersMap.get(date)?.buys;
      dataMap.set(date, entry);
    }

    for (const [date, fdv] of orderMapSell.entries()) {
      const entry = dataMap.get(date) || { date };
      entry.secondLaneSell = fdv;
      entry.allSellOrders = allOrdersMap.get(date)?.sells;
      dataMap.set(date, entry);
    }
  }
  
  return dataMap;
};

// Function for sorting data by date
const sortDataByDate = (dataMap: Map<string, GraphDataPoint>): GraphDataPoint[] => {
  return Array.from(dataMap.values())
    .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
};

const ProjectChart: React.FC<ProjectChartProps> = ({ graphData, loading = false }) => {
  const [chartData, setChartData] = useState<GraphDataPoint[]>([]);
  const [hasFundingValuation, setHasFundingValuation] = useState(false);
  const [shouldShowChart, setShouldShowChart] = useState(true);

  // Note: All values on the chart (Y-axis) are represented in FDV (Fully Diluted Valuation)
  // The black line represents market cap (from market_cap_usd)
  // The red line represents funding rounds valuation
  // The green bars represent SecondLane Buy orders
  // The hatched bars represent SecondLane Sell orders
  
  useEffect(() => {
    if (!graphData) {
      setShouldShowChart(false);
      return;
    }



    // Check if we should show the chart
    const hasOrders = graphData.orders && graphData.orders.length > 0;
    const hasPriceHistory = graphData.price_history && graphData.price_history.length > 0;
    
    // Check if any funding round has FDV and it's not zero
    let anyFundingRoundHasFDV = false;
    if (graphData.funding_rounds && graphData.funding_rounds.length > 0) {
      anyFundingRoundHasFDV = graphData.funding_rounds.some(round => 
        round.valuation !== null && round.valuation > 0
      );
      setHasFundingValuation(anyFundingRoundHasFDV);
    }

    // Check if price history has non-zero values
    let hasNonZeroPriceHistory = false;
    if (hasPriceHistory) {
      hasNonZeroPriceHistory = graphData.price_history!.some(
        item => item.spot_fdv_usd > 0
      );
    }

    // Check if orders have non-zero values
    let hasNonZeroOrders = false;
    if (hasOrders) {
      hasNonZeroOrders = graphData.orders!.some(
        order => order.offered_fully_diluted_valuation > 0
      );
    }

    // If there are funding rounds but no FDV or only zero FDVs, no orders or only zero orders, 
    // and no price history or only zero market caps, we should not show the chart
    if ((graphData.funding_rounds && 
        graphData.funding_rounds.length > 0 && 
        !anyFundingRoundHasFDV) && 
        (!hasOrders || !hasNonZeroOrders) && 
        (!hasPriceHistory || !hasNonZeroPriceHistory)) {
      setShouldShowChart(false);
      return;
    }

    // Initialization of dataMap for storing chart data
    let dataMap = new Map<string, GraphDataPoint>();

    // Processing different types of data
    dataMap = processPriceHistory(graphData.price_history, dataMap);
    dataMap = processFundingRounds(graphData.funding_rounds, dataMap);
    dataMap = processOrders(graphData.orders, dataMap);

    // Sorting data by date
    const sortedData = sortDataByDate(dataMap);

    setChartData(sortedData);
  }, [graphData]);

  if (loading) {
    return (
      <div className="bg-[rgba(255,255,255,0.72)] dark:bg-[rgba(28,28,28,0.72)] border border-white dark:border-[rgba(255,255,255,0.1)] p-6">
        <Skeleton className="h-[350px] w-full" />
      </div>
    );
  }

  if (!shouldShowChart || !chartData.length) {
    return (
      <div className="bg-[rgba(255,255,255,0.72)] dark:bg-[rgba(28,28,28,0.72)] border border-white dark:border-[rgba(255,255,255,0.1)] p-6">
        <div className="text-center py-8">
          <p className="font-['Plus_Jakarta_Sans'] text-[rgba(45,41,35,0.64)] dark:text-[rgba(253,253,253,0.64)]">
            No chart data available for this project
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-[rgba(255,255,255,0.72)] dark:bg-[rgba(28,28,28,0.72)] border border-white dark:border-[rgba(255,255,255,0.1)] p-6">
      <div className="w-full h-[320px]">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart 
            data={chartData}
            margin={{ top: 20, right: 20, left: 10, bottom: 10 }}
          >
            <CartesianGrid 
              vertical={false} 
              horizontal={true} 
              stroke="#878787"
              strokeDasharray="4 4"
              opacity={0.3}
            />
            <XAxis 
              dataKey="date" 
              tick={{ 
                fontSize: 11, 
                fill: "#878787",
                fontFamily: "Plus Jakarta Sans"
              }}
              tickMargin={10}
              stroke="transparent"
              axisLine={false}
              tickLine={false}
            />
            <YAxis 
              tickFormatter={formatCurrency}
              tick={{ 
                fontSize: 13, 
                fill: "#878787",
                fontFamily: "Plus Jakarta Sans"
              }}
              width={60}
              stroke="transparent"
              axisLine={false}
              tickLine={false}
            />
            
            <Tooltip 
              contentStyle={{
                backgroundColor: "#fff",
                border: "1px solid rgba(45,41,35,0.12)",
                color: "#2d2923",
                borderRadius: "0",
                fontFamily: "Plus Jakarta Sans"
              }}
              formatter={(value: number, name: string, entry: {payload?: GraphDataPoint}) => {
                const formattedValue = formatCurrency(value);
                const labels = {
                  marketValue: 'Spot FDV',
                  fundingValue: 'Funding FDV',
                  secondLaneBuy: 'SecondLane Buy',
                  secondLaneSell: 'SecondLane Sell'
                };
                
                if (name === 'marketValue') {
                  return [formattedValue, labels[name as keyof typeof labels]];
                }
                
                if (entry?.payload) {
                  const data = entry.payload;
                  
                  if (name === 'secondLaneBuy' && data.allBuyOrders && data.allBuyOrders.length > 1) {
                    const allOrders = data.allBuyOrders.map((order) => 
                      `- ${formatCurrency(order.fdv)} (${formatCurrency(order.amount)})`
                    ).join('\n');
                    return [`${formattedValue}\nAll Buy Orders:\n${allOrders}`, labels[name as keyof typeof labels]];
                  }
                  
                  if (name === 'secondLaneSell' && data.allSellOrders && data.allSellOrders.length > 1) {
                    const allOrders = data.allSellOrders.map((order) => 
                      `- ${formatCurrency(order.fdv)} (${formatCurrency(order.amount)})`
                    ).join('\n');
                    return [`${formattedValue}\nAll Sell Orders:\n${allOrders}`, labels[name as keyof typeof labels]];
                  }
                }
                
                return [formattedValue, labels[name as keyof typeof labels]];
              }}
              labelFormatter={(label) => `Date: ${label}`}
            />
            
            {/* Spot FDV - Green/Lime line */}
            <Line
              type="monotone"
              dataKey="marketValue"
              stroke="#9db807"
              strokeWidth={2}
              dot={false}
              connectNulls
            />
            
            {/* Funding FDV - Orange line */}
            {hasFundingValuation && (
              <Line
                type="stepAfter"
                dataKey="fundingValue"
                stroke="#f59e0b"
                strokeWidth={2}
                dot={false}
                connectNulls
              />
            )}
            
            {/* SecondLane Buy - Light blue squares */}
            <Scatter
              dataKey="secondLaneBuy"
              fill="#7dd3fc"
              shape={(props: ScatterShapeProps) => <SquareShape {...props} fill="#7dd3fc" dataKey="secondLaneBuy" />}
            />
            
            {/* SecondLane Sell - Gray squares */}
            <Scatter
              dataKey="secondLaneSell"
              fill="#a1a1aa"
              shape={(props: ScatterShapeProps) => <SquareShape {...props} fill="#a1a1aa" dataKey="secondLaneSell" />}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
      
      {/* Legend */}
      <div className="mt-6 flex flex-wrap justify-center gap-8">
        <div className="flex items-center gap-3">
          <div className="w-[19px] h-[8px] bg-[#9db807]" />
          <span className="font-['Plus_Jakarta_Sans'] font-medium text-[14px] leading-[20px] text-[#2d2923] dark:text-[#fdfdfd]">
            Spot FDV
          </span>
        </div>
        {hasFundingValuation && (
          <div className="flex items-center gap-3">
            <div className="w-[19px] h-[8px] bg-[#f59e0b]" />
            <span className="font-['Plus_Jakarta_Sans'] font-medium text-[14px] leading-[20px] text-[#2d2923] dark:text-[#fdfdfd]">
              Funding FDV
            </span>
          </div>
        )}
        <div className="flex items-center gap-3">
          <div className="w-[14px] h-[14px] bg-[#7dd3fc]" />
          <span className="font-['Plus_Jakarta_Sans'] font-medium text-[14px] leading-[20px] text-[#2d2923] dark:text-[#fdfdfd]">
            SecondLane Buy
          </span>
        </div>
        <div className="flex items-center gap-3">
          <div className="w-[14px] h-[14px] bg-[#a1a1aa]" />
          <span className="font-['Plus_Jakarta_Sans'] font-medium text-[14px] leading-[20px] text-[#2d2923] dark:text-[#fdfdfd]">
            SecondLane Sell
          </span>
        </div>
      </div>
    </div>
  );
};

export default ProjectChart;
